import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  Group,
  type GroupImperativeHandle,
  type GroupProps,
  Separator,
  type SeparatorProps,
  useDefaultLayout,
} from "react-resizable-panels";

import { cn, setRefs } from "../utils/general";
import { Icon } from "./icon";

type PanelGroupProps = GroupProps & {
  autoSaveId?: string;
  direction?: GroupProps["orientation"];
  panelIds?: string[];
};

type PanelLayout = NonNullable<GroupProps["defaultLayout"]>;

const hasPanelLayout = (layout: GroupProps["defaultLayout"], panelIds: string[] | undefined): layout is PanelLayout => {
  if (!layout || !panelIds) {
    return false;
  }

  return (
    Object.keys(layout).length === panelIds.length &&
    panelIds.every((panelId) => {
      const panelSize = layout[panelId];

      return typeof panelSize === "number" && Number.isFinite(panelSize);
    })
  );
};

const PanelGroupRoot = ({ className, direction, orientation, panelIds: _panelIds, ...props }: PanelGroupProps) => (
  <Group className={cn("flex h-full w-full", className)} orientation={orientation ?? direction} {...props} />
);

const AutoSavePanelGroup = ({
  autoSaveId,
  defaultLayout,
  groupRef,
  onLayoutChange,
  onLayoutChanged,
  panelIds,
  ...props
}: PanelGroupProps & { autoSaveId: string }) => {
  const autoSaveGroupRef = useRef<GroupImperativeHandle | null>(null);
  const savedLayout = useDefaultLayout({ id: autoSaveId, panelIds });
  const restoredLayout = useMemo(
    () =>
      hasPanelLayout(savedLayout.defaultLayout, panelIds)
        ? savedLayout.defaultLayout
        : hasPanelLayout(defaultLayout, panelIds)
          ? defaultLayout
          : undefined,
    [defaultLayout, panelIds, savedLayout.defaultLayout]
  );
  const panelSetKey = panelIds?.join(":") ?? "";
  const handleGroupRef = useMemo(() => setRefs(autoSaveGroupRef, groupRef), [groupRef]);

  const handleLayoutChange = useCallback<NonNullable<GroupProps["onLayoutChange"]>>(
    (layout) => {
      savedLayout.onLayoutChange?.(layout);
      onLayoutChange?.(layout);
    },
    [onLayoutChange, savedLayout.onLayoutChange]
  );

  const handleLayoutChanged = useCallback<NonNullable<GroupProps["onLayoutChanged"]>>(
    (layout) => {
      savedLayout.onLayoutChanged?.(layout);
      onLayoutChanged?.(layout);
    },
    [onLayoutChanged, savedLayout.onLayoutChanged]
  );

  useEffect(() => {
    const group = autoSaveGroupRef.current;

    if (group && hasPanelLayout(restoredLayout, panelIds) && hasPanelLayout(group.getLayout(), panelIds)) {
      group.setLayout(restoredLayout);
    }
  }, [panelIds, panelSetKey, restoredLayout]);

  return (
    <PanelGroupRoot
      defaultLayout={restoredLayout}
      groupRef={handleGroupRef}
      onLayoutChange={handleLayoutChange}
      onLayoutChanged={handleLayoutChanged}
      panelIds={panelIds}
      {...props}
    />
  );
};

const PanelGroup = ({ autoSaveId, ...props }: PanelGroupProps) =>
  autoSaveId ? <AutoSavePanelGroup autoSaveId={autoSaveId} {...props} /> : <PanelGroupRoot {...props} />;

// react-resizable-panels persists each autosaved group under a key of the form
// `react-resizable-panels:<autoSaveId>:<panelId>:...`. The prefix is the
// library's; it lives here — next to the autosave wrapper that owns it — so
// callers can clear persisted layouts without reconstructing the key scheme.
const PANEL_AUTOSAVE_STORAGE_PREFIX = "react-resizable-panels:";

/** Every localStorage key that holds a persisted `autoSaveId` panel layout. */
const getAutoSavedLayoutKeys = (storage: Storage): string[] => {
  const keys: string[] = [];

  for (let index = 0; index < storage.length; index++) {
    const key = storage.key(index);

    if (key?.startsWith(PANEL_AUTOSAVE_STORAGE_PREFIX)) {
      keys.push(key);
    }
  }

  return keys;
};

// Zero the separator in both orientations: width for vertical separators
// (horizontal groups) and height for horizontal separators (vertical groups).
const HIDDEN_PANEL_HANDLE_CLASS_NAME =
  "w-0 bg-transparent after:w-0 aria-[orientation=horizontal]:h-0 aria-[orientation=horizontal]:after:h-0";

const PanelHandle = ({
  withHandle,
  hidden,
  className,
  disabled,
  ...props
}: SeparatorProps & {
  withHandle?: boolean;
  hidden?: boolean;
}) => (
  <Separator
    draggable={false}
    disabled={hidden || disabled}
    className={cn(
      // aria-orientation describes the separator; horizontal panel groups render vertical separators.
      "relative flex w-px select-none items-center justify-center bg-border transition-colors after:absolute after:inset-y-0 after:left-1/2 after:w-1 after:-translate-x-1/2 after:select-none focus-visible:bg-ring focus-visible:outline-none data-[separator=active]:bg-ring data-[separator=hover]:bg-ring/60 aria-[orientation=horizontal]:h-px aria-[orientation=horizontal]:w-full aria-[orientation=horizontal]:after:left-0 aria-[orientation=horizontal]:after:h-1 aria-[orientation=horizontal]:after:w-full aria-[orientation=horizontal]:after:-translate-y-1/2 aria-[orientation=horizontal]:after:translate-x-0 [&[aria-orientation=horizontal]>div]:rotate-90",
      hidden && HIDDEN_PANEL_HANDLE_CLASS_NAME,
      className
    )}
    {...props}
  >
    {withHandle && !hidden && (
      <div className="z-10 flex h-4 w-3 items-center justify-center rounded-sm border bg-border">
        <Icon icon="drag_indicator" className="size-2.5" />
      </div>
    )}
  </Separator>
);

export type { GroupImperativeHandle, PanelImperativeHandle } from "react-resizable-panels";
export { Panel } from "react-resizable-panels";
export { getAutoSavedLayoutKeys, PanelGroup, PanelHandle };
